// Utility functions for card view
import moment from 'moment'

export function safe(v) {
  if (!v) return '—'
  const s = String(v).trim()
  if (!s || s === '--') return '—'
  return s
}

export function shortDate(str) {
  if (!str) return '—'
  // "02-07-2024 05:30 am" -> "02-07-2024"
  return moment(str).format('DD-MM-YYYY')
}

export function parseDateOnly(str) {
  if (!str) return null
  const trimmed = String(str).trim()
  if (!trimmed) return null

  // "02-07-2024 05:30 am" -> "02-07-2024"
  const firstPart = trimmed.split(' ')[0]
  const sep = firstPart.includes('-')
    ? '-'
    : firstPart.includes('/')
      ? '/'
      : null
  if (!sep) return null
  const parts = firstPart.split(sep)
  if (parts.length !== 3) return null

  const [ddStr, mmStr, yyyyStr] = parts
  const dd = parseInt(ddStr, 10)
  const mm = parseInt(mmStr, 10)
  const yyyy = parseInt(yyyyStr, 10)
  if (!dd || !mm || !yyyy) return null

  return new Date(yyyy, mm - 1, dd) // local midnight
}

export function getDaysSinceComplaint(card) {
  // console.log('card', card)
  const commplaintDate = moment(card.complaintDate)
  // console.log('commplaintDate', commplaintDate)
  const today = moment()
  const daysDiff = today.diff(commplaintDate, 'days')
  return daysDiff
}

export function isExpectedCompletionPast(card) {
  const d = parseDateOnly(card.expectedCompletion)
  if (!d) return false

  const today = new Date()
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate())

  return dOnly < todayOnly
}

export function isReviewDatePast(card) {
  if (!card.reviewDate) return false

  const reviewDate = moment(card.reviewDate)
  if (!reviewDate.isValid()) return false

  const today = moment().startOf('day')
  const reviewDateOnly = reviewDate.startOf('day')

  // Return true if review date is less than today (before today)
  return reviewDateOnly.isBefore(today)
}

export function getReviewDateCategory(card) {
  if (!card.reviewDate) return 'beyond7days' // default to future if no date

  const reviewDate = moment(card.reviewDate)
  if (!reviewDate.isValid()) return 'beyond7days' // default if invalid date

  const today = moment().startOf('day')
  const reviewDateOnly = reviewDate.startOf('day')

  // Calculate days difference using moment
  const daysDiff = reviewDateOnly.diff(today, 'days')

  if (daysDiff < -1) return 'earlierThanYesterday' // Before yesterday
  if (daysDiff === -1) return 'yesterday' // Yesterday
  if (daysDiff === 0) return 'today' // Today
  if (daysDiff <= 7) return 'next7days' // Next 7 days
  return 'beyond7days' // Beyond 7 days
}

export function hslToRgb(hslString) {
  // Handle both HSL format and hex/rgb formats from database
  if (!hslString) return { r: 128, g: 128, b: 128 } // Default grey

  // If it's already a hex color or rgb, parse it differently
  if (hslString.startsWith('#')) {
    const hex = hslString.replace('#', '')
    return {
      r: parseInt(hex.substring(0, 2), 16),
      g: parseInt(hex.substring(2, 4), 16),
      b: parseInt(hex.substring(4, 6), 16)
    }
  }

  if (hslString.startsWith('rgb')) {
    const match = hslString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
    if (match) {
      return {
        r: parseInt(match[1]),
        g: parseInt(match[2]),
        b: parseInt(match[3])
      }
    }
  }

  // Parse HSL format
  const match = hslString.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)
  if (!match) return { r: 128, g: 128, b: 128 } // Default grey

  const h = parseInt(match[1]) / 360
  const s = parseInt(match[2]) / 100
  const l = parseInt(match[3]) / 100

  let r, g, b
  if (s === 0) {
    r = g = b = l
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  }
}

export function getPresentStatusColor(presentStatus) {
  if (!presentStatus) return null

  // If color code comes from database, use it
  // For now, generate a deterministic color based on the status text
  const status = presentStatus.trim()

  // Simple hash function to generate consistent color
  let hash = 0
  for (let i = 0; i < status.length; i++) {
    hash = status.charCodeAt(i) + ((hash << 5) - hash)
  }

  // Generate a color from the hash (using HSL for better color distribution)
  const hue = Math.abs(hash % 360)
  // Use medium saturation and lightness for readable colors
  const saturation = 60 + (Math.abs(hash) % 20) // 60-80%
  const lightness = 45 + (Math.abs(hash >> 8) % 15) // 45-60%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

